import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next'; // Importer TFunction directement depuis i18next

// Types pour notre contexte de traduction
interface TranslationContextType {
  language: string;
  changeLanguage: (lang: string) => Promise<void>;
  t: TFunction; // Utiliser le type TFunction importé de i18next
  availableLanguages: string[];
}

// Création du contexte avec des valeurs par défaut
const TranslationContext = createContext<TranslationContextType>({
  language: 'fr',
  changeLanguage: async () => {},
  // Utiliser une fonction compatible avec TFunction
  t: ((key: string) => key) as unknown as TFunction,
  availableLanguages: ['fr', 'en'],
});

// Hook pour utiliser le contexte de traduction
export const useTranslationContext = () => useContext(TranslationContext);

interface TranslationProviderProps {
  children: React.ReactNode;
}

/**
 * Fournisseur de contexte de traduction qui encapsule la logique i18n
 * et fournit des méthodes utilitaires pour gérer les traductions.
 */
export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const [language, setLanguage] = useState(i18nInstance.language || 'fr');

  // Liste des langues disponibles dans l'application
  const availableLanguages = ['fr', 'en'];

  // Mettre à jour la langue actuelle lorsque i18n change
  useEffect(() => {
    setLanguage(i18nInstance.language);
  }, [i18nInstance.language]);

  /**
   * Changer la langue de l'application
   * @param lang - Code de langue (fr, en, etc.)
   */
  const changeLanguage = async (lang: string) => {
    if (availableLanguages.includes(lang)) {
      await i18nInstance.changeLanguage(lang);
      localStorage.setItem('i18nextLng', lang);
      setLanguage(lang);
    }
  };

  // Valeur du contexte à exposer avec un typage explicite
  const value: TranslationContextType = {
    language,
    changeLanguage,
    t,
    availableLanguages,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationContext;
